import { createStore } from 'zustand/vanilla'

import type { ReleaseType } from './VersionComparator'

export type VersionState = {
  /** Versão semântica assada neste bundle. `null` quando o build não a resolveu. */
  currentVersion: string | null
  /** Última versão que o servidor reportou como publicada. */
  latestVersion: string | null
  /** Tipo do salto entre as duas, decidido pelo diff local — não pelo payload. */
  releaseType: ReleaseType | null
  updateAvailable: boolean
  /** ISO-8601 da última checagem, bem ou mal sucedida. */
  lastCheck: string | null
  /** Versão cujo modal de major o usuário já dispensou. */
  dismissedVersion: string | null
  /**
   * Razões que impedem um reload agora. Preenchido por `useReloadGuard`; a
   * política só olha o tamanho, nunca o conteúdo.
   */
  blockingReasons: Set<string>

  addBlock: (reason: string) => void
  removeBlock: (reason: string) => void
  setLatest: (result: {
    latestVersion: string | null
    releaseType: ReleaseType | null
    lastCheck: string
  }) => void
  markCheckFailed: (lastCheck: string) => void
  dismissUpdate: (version: string) => void
}

/**
 * Store vanilla do Zustand — sem `create`, sem hook. O núcleo do serviço
 * (`VersionChecker`, `UpdatePolicy`, `IdleManager`) lê e escreve daqui sem
 * tocar em React; a árvore de componentes entra pelo `useStore` no provider.
 *
 * Fábrica em vez de singleton exportado para que cada teste parta de um estado
 * limpo sem depender de ordem de execução.
 */
export const createVersionStore = (
  initial: { currentVersion?: string | null } = {}
) =>
  createStore<VersionState>()((set, get) => ({
    currentVersion: initial.currentVersion ?? null,
    latestVersion: null,
    releaseType: null,
    updateAvailable: false,
    lastCheck: null,
    dismissedVersion: null,
    blockingReasons: new Set<string>(),

    // Toda mutação do Set cria uma instância nova: Zustand compara referências,
    // e um `Set` mutado in place mudaria o estado sem notificar assinante
    // nenhum — a guarda pareceria montada e não seguraria reload algum.
    addBlock: reason => {
      const { blockingReasons } = get()

      if (blockingReasons.has(reason)) return

      const próximo = new Set(blockingReasons)
      próximo.add(reason)

      set({ blockingReasons: próximo })
    },

    removeBlock: reason => {
      const { blockingReasons } = get()

      if (!blockingReasons.has(reason)) return

      const próximo = new Set(blockingReasons)
      próximo.delete(reason)

      set({ blockingReasons: próximo })
    },

    setLatest: ({ latestVersion, releaseType, lastCheck }) =>
      set({
        latestVersion,
        releaseType,
        lastCheck,
        updateAvailable: releaseType !== null
      }),

    // Falha de checagem só move o relógio. A atualização já detectada continua
    // pendente: ela está esperando o app ficar ocioso, e um endpoint instável
    // no meio do caminho não é motivo para esquecer que ela existe.
    markCheckFailed: lastCheck => set({ lastCheck }),

    dismissUpdate: version => set({ dismissedVersion: version })
  }))

export type VersionStore = ReturnType<typeof createVersionStore>
